import { serviceRoleClient } from './serviceRoleClient';
import { ENV, SUPABASE_URL } from '../config/env';

const BULK_BATCH_SIZE = 80;
/** Stall fallback only — primary send is edge worker (direct Meta). */
const BULK_CONCURRENCY = 12;
const BULK_CONCURRENCY_THROTTLED = 4;
const BULK_INSERT_CHUNK = 1000;
const BULK_POPULATE_CHUNK = 5000;
const STATS_REFRESH_EVERY = 3;
const SYSTEM_USER_ID = ENV.WHATSAPP_SYSTEM_USER_ID || '879ce3a2-ad29-4953-996b-7d01b77d14d2';

const activeCampaigns = new Set();

function isRetryableSendFailure(httpStatus, errorText) {
  const msg = String(errorText || '').toLowerCase();
  if (httpStatus === 429 || httpStatus === 546 || httpStatus === 502 || httpStatus === 503 || httpStatus === 504) {
    return true;
  }
  return (
    msg.includes('rate limit')
    || msg.includes('retry after')
    || msg.includes('temporar')
    || msg.includes('timeout')
    ||     msg.includes('546')
    || msg.includes('429')
    || msg.includes('130429')
    || msg.includes('131056')
    || msg.includes('overloaded')
    || msg.includes('throughput')
  );
}

function parseRetryAfterMs(errorText, httpStatus) {
  const match = String(errorText || '').match(/retry after\s*(\d+)\s*ms/i);
  if (match) return Math.min(Number(match[1]) + 500, 60_000);
  if (httpStatus === 429 || httpStatus === 546) return 15_000;
  return 2000;
}

const fnHeaders = {
  'Content-Type': 'application/json',
  apikey: ENV.SUPABASE_ANON_KEY,
  Authorization: `Bearer ${ENV.SUPABASE_ANON_KEY}`,
};

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function mapPool(items, concurrency, fn) {
  let idx = 0;
  async function worker() {
    while (idx < items.length) {
      const current = idx++;
      await fn(items[current], current);
    }
  }
  const n = Math.min(Math.max(concurrency, 1), Math.max(items.length, 1));
  await Promise.all(Array.from({ length: n }, () => worker()));
}

function normalizePhone(raw) {
  const digits = String(raw || '').replace(/[^\d]/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `91${digits}`;
  if (digits.startsWith('91') && digits.length === 12) return digits;
  return digits;
}

function fillTemplate(text, vars = {}) {
  let out = text || '';
  Object.entries(vars).forEach(([key, val], idx) => {
    const safe = String(val ?? '')
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/ {5,}/g, '    ')
      .trim() || '-';
    out = out.replace(new RegExp(`\\{\\{${idx + 1}\\}\\}`, 'g'), safe);
    out = out.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'gi'), safe);
  });
  return out;
}

function sanitizeParam(value) {
  const text = String(value ?? '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/ {5,}/g, '    ')
    .trim();
  return text || '-';
}

/** Supports legacy array or { body, header_type, header_image_url }. */
function parseCampaignTemplateConfig(raw) {
  if (Array.isArray(raw)) {
    return { bodyParams: raw, header_type: '', header_image_url: '' };
  }
  if (raw && typeof raw === 'object') {
    const bodyParams = Array.isArray(raw.body) ? raw.body
      : (Array.isArray(raw.params) ? raw.params : []);
    return {
      bodyParams,
      header_type: String(raw.header_type || ''),
      header_image_url: String(raw.header_image_url || ''),
    };
  }
  return { bodyParams: [], header_type: '', header_image_url: '' };
}

function extractFirstUrl(text) {
  const match = String(text || '').match(/https?:\/\/[^\s]+/i);
  return match ? match[0].replace(/[.,;!?]+$/, '') : null;
}

function buildVars(recipient) {
  const v = recipient.variables || {};
  const base = {
    customer_name: String(v.customer_name || recipient.contact_name || 'Customer'),
    phone: String(v.phone || recipient.phone || ''),
    membership_number: String(v.membership_number || ''),
    plan_name: String(v.plan_name || ''),
    start_date: String(v.start_date || ''),
    expiry_date: String(v.expiry_date || ''),
    membership_status: String(v.membership_status || ''),
    car: String(v.car || ''),
    car_model: String(v.car_model || ''),
    state: String(v.state || ''),
    pincode: String(v.pincode || ''),
    email: String(v.email || ''),
    status: String(v.status || ''),
    content: String(v.content || ''),
  };
  Object.entries(v).forEach(([key, val]) => {
    if (!(key in base)) base[key] = String(val ?? '');
  });
  return base;
}

function wrapTrackLink(recipientId) {
  return `${SUPABASE_URL}/functions/v1/whatsapp-bulk-link?r=${recipientId}`;
}

function buildSendPayload(campaign, recipient) {
  const vars = buildVars(recipient);
  const payload = {
    to: recipient.phone,
    user_id: SYSTEM_USER_ID,
    account_id: campaign.account_id || null,
    source: 'bulk',
    contact_name: vars.customer_name,
  };

  if (campaign.message_mode === 'template' && campaign.template_name) {
    const cfg = parseCampaignTemplateConfig(campaign.template_params);
    const templateParams = cfg.bodyParams.map((p) => sanitizeParam(fillTemplate(String(p), vars)));
    payload.type = 'template';
    payload.template_name = campaign.template_name;
    payload.template_language = campaign.template_language || 'en';
    payload.template_params = templateParams;
    if (cfg.header_type && cfg.header_type !== 'NONE') {
      payload.template_header_type = cfg.header_type;
    }
    if (cfg.header_image_url) {
      payload.template_header_image_url = cfg.header_image_url;
    }
  } else {
    let textContent = fillTemplate(campaign.text_body || '', vars);
    if (campaign.track_links && recipient.target_url && recipient.id) {
      const originalUrl = extractFirstUrl(textContent) || recipient.target_url;
      if (originalUrl) textContent = textContent.replace(originalUrl, wrapTrackLink(recipient.id));
    }
    payload.type = 'text';
    payload.content = textContent;
  }

  return payload;
}

async function countRecipients(campaignId, status) {
  let q = serviceRoleClient
    .from('whatsapp_bulk_campaign_recipients')
    .select('id', { count: 'exact', head: true })
    .eq('campaign_id', campaignId);
  if (status) q = q.eq('status', status);
  const { count } = await q;
  return Number(count || 0);
}

async function ensureSegmentTotal(campaign) {
  let total = Number(campaign.total_recipients || 0);
  if (total > 0) return total;
  if (!(campaign.segment || campaign.search || campaign.state_filter)) return 0;

  if (campaign.segment === 'wfh') {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_list_wfh_leads', {
      p_team_lead_id: campaign.state_filter,
      p_batch_name: campaign.search || '',
      p_search: '',
      p_limit: 1,
      p_offset: 0,
    });
    if (error) return 0;
    total = Number(data?.total || 0);
  } else if (String(campaign.segment || '').startsWith('mechanics')) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_list_mechanics', {
      p_segment: campaign.segment || 'mechanics',
      p_search: campaign.search || '',
      p_state: campaign.state_filter || '',
      p_limit: 1,
      p_offset: 0,
    });
    if (error) return 0;
    total = Number(data?.total || 0);
  } else {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_list_customers', {
      p_segment: campaign.segment || 'all',
      p_search: campaign.search || '',
      p_state: campaign.state_filter || '',
      p_limit: 1,
      p_offset: 0,
    });
    if (error) return 0;
    total = Number(data?.total || 0);
  }

  if (total > 0) {
    await serviceRoleClient
      .from('whatsapp_bulk_campaigns')
      .update({ total_recipients: total })
      .eq('id', campaign.id);
    campaign.total_recipients = total;
  }
  return total;
}

async function populateFromSegment(campaign) {
  const inserted = await countRecipients(campaign.id);
  let total = await ensureSegmentTotal(campaign);
  if (total > 0 && inserted >= total) return 0;

  if (campaign.segment === 'wfh') {
    const { data: enqueued, error: enqueueErr } = await serviceRoleClient.rpc(
      'whatsapp_bulk_enqueue_wfh_recipients',
      {
        p_campaign_id: campaign.id,
        p_limit: BULK_POPULATE_CHUNK,
        p_offset: inserted,
      },
    );
    if (enqueueErr) throw enqueueErr;
    return Number(enqueued || 0);
  }

  if (String(campaign.segment || '').startsWith('mechanics')) {
    const skipEnqueueRpc = campaign.segment === 'mechanics_not_linked';
    if (!skipEnqueueRpc) {
      const { data: enqueued, error: enqueueErr } = await serviceRoleClient.rpc(
        'whatsapp_bulk_enqueue_mechanic_recipients',
        {
          p_campaign_id: campaign.id,
          p_limit: BULK_POPULATE_CHUNK,
          p_offset: inserted,
        },
      );
      if (!enqueueErr) return Number(enqueued || 0);
      if (!/could not find|does not exist|42883|duplicate key|23505/i.test(enqueueErr.message || '')) {
        throw enqueueErr;
      }
    }
    let items = [];
    const { data, error } = skipEnqueueRpc
      ? { data: null, error: { message: 'does not exist' } }
      : await serviceRoleClient.rpc('whatsapp_bulk_list_mechanics', {
      p_segment: campaign.segment || 'mechanics',
      p_search: campaign.search || '',
      p_state: campaign.state_filter || '',
      p_limit: BULK_POPULATE_CHUNK,
      p_offset: inserted,
    });
    if (error) {
      if (!/could not find|does not exist|42883/i.test(error.message || '')) throw error;
      let q = serviceRoleClient
        .from('mechanics')
        .select('id, mechanic_name, shop_name, number, alternate_number1, code, city, area, partner_type, active, owner_user_id, previous_assigned_cases, service_areas')
        .order('mechanic_name', { ascending: true, nullsFirst: false })
        .or('number.neq.,alternate_number1.neq.')
        .range(inserted, inserted + BULK_POPULATE_CHUNK - 1);
      if (campaign.segment === 'mechanics_active') q = q.eq('active', true);
      if (campaign.segment === 'mechanics_inactive') q = q.eq('active', false);
      if (campaign.segment === 'mechanics_not_linked') q = q.is('owner_user_id', null);
      const { data: rows, error: e2 } = await q;
      if (e2) throw e2;
      items = (rows || []).map((row) => {
        const digits = String(row.number || row.alternate_number1 || '').replace(/\D/g, '');
        const phone = digits.length >= 10 ? digits.slice(-10) : '';
        const areas = Array.isArray(row.service_areas) ? row.service_areas : [];
        return {
          phone,
          mechanic_name: row.mechanic_name || row.shop_name || 'Partner',
          code: row.code || '',
          shop_name: row.shop_name || '',
          city: row.city || '',
          area: row.area || '',
          state: areas.find((a) => a?.state)?.state || '',
          pincode: areas.find((a) => a?.pincode)?.pincode || '',
          partner_type: row.partner_type || '',
          previous_cases: String(row.previous_assigned_cases || 0),
        };
      }).filter((row) => row.phone);
    } else {
      items = Array.isArray(data?.items) ? data.items : [];
    }
    if (!items.length) return 0;
    const rows = items.map((row) => {
      const name = row.mechanic_name || row.customer_name || 'Partner';
      const variables = {
        customer_name: name,
        mechanic_name: name,
        phone: row.phone || '',
        code: row.code || '',
        shop_name: row.shop_name || '',
        city: row.city || '',
        area: row.area || '',
        state: row.state || '',
        pincode: row.pincode || '',
        partner_type: row.partner_type || '',
        previous_cases: String(row.previous_cases ?? row.previous_assigned_cases ?? 0),
        previous_assigned_cases: String(row.previous_cases ?? row.previous_assigned_cases ?? 0),
      };
      return {
        campaign_id: campaign.id,
        phone: normalizePhone(row.phone),
        contact_name: name,
        variables,
        status: 'pending',
      };
    }).filter((r) => r.phone);
    const seenPhones = new Set();
    const uniqueRows = rows.filter((r) => {
      const phone10 = String(r.phone || '').replace(/\D/g, '').slice(-10);
      if (!phone10 || seenPhones.has(phone10)) return false;
      seenPhones.add(phone10);
      return true;
    });
    let insertedNow = 0;
    for (let i = 0; i < uniqueRows.length; i += 200) {
      const chunk = uniqueRows.slice(i, i + 200);
      const { error: insertErr } = await serviceRoleClient
        .from('whatsapp_bulk_campaign_recipients')
        .insert(chunk);
      if (!insertErr) {
        insertedNow += chunk.length;
        continue;
      }
      if (!/duplicate key|23505/i.test(insertErr.message || '')) throw insertErr;
      for (const row of chunk) {
        const { error: oneErr } = await serviceRoleClient
          .from('whatsapp_bulk_campaign_recipients')
          .insert(row);
        if (!oneErr) insertedNow += 1;
      }
    }
    return insertedNow;
  }

  const { data: enqueued, error: enqueueErr } = await serviceRoleClient.rpc(
    'whatsapp_bulk_enqueue_segment_recipients',
    {
      p_campaign_id: campaign.id,
      p_limit: BULK_POPULATE_CHUNK,
      p_offset: inserted,
    },
  );
  if (!enqueueErr) {
    const n = Number(enqueued || 0);
    if (n === 0 && inserted === 0) {
      // Cache may be empty for this segment — try list path below
    } else {
      return n;
    }
  } else if (!/could not find|does not exist|42883/i.test(enqueueErr.message || '')) {
    throw enqueueErr;
  }

  const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_list_customers', {
    p_segment: campaign.segment || 'all',
    p_search: campaign.search || '',
    p_state: campaign.state_filter || '',
    p_limit: BULK_POPULATE_CHUNK,
    p_offset: inserted,
  });
  if (error) throw error;

  const listTotal = Number(data?.total || 0);
  if (listTotal > Number(campaign.total_recipients || 0)) {
    await serviceRoleClient
      .from('whatsapp_bulk_campaigns')
      .update({ total_recipients: listTotal })
      .eq('id', campaign.id);
    campaign.total_recipients = listTotal;
  }

  const items = Array.isArray(data?.items) ? data.items : [];
  if (!items.length) return 0;

  const rows = items.map((row) => {
    const variables = {
      customer_name: row.customer_name || 'Customer',
      phone: row.phone || '',
      membership_number: row.membership_number || '',
      plan_name: row.plan_name || '',
      start_date: row.start_date || '',
      expiry_date: row.expiry_date || '',
      membership_status: row.membership_status || '',
      car: row.car || '',
      car_model: row.car_model || '',
      state: row.state || '',
      pincode: row.pincode || '',
      email: row.email || '',
    };
    const recipient = { phone: row.phone, contact_name: variables.customer_name, variables };
    return {
      campaign_id: campaign.id,
      phone: normalizePhone(row.phone),
      contact_name: variables.customer_name,
      variables,
      status: 'pending',
      target_url: campaign.track_links && campaign.message_mode !== 'template'
        ? extractFirstUrl(fillTemplate(campaign.text_body || '', buildVars(recipient)))
        : null,
    };
  }).filter((r) => r.phone);

  for (let i = 0; i < rows.length; i += BULK_INSERT_CHUNK) {
    const { error: insertErr } = await serviceRoleClient
      .from('whatsapp_bulk_campaign_recipients')
      .insert(rows.slice(i, i + BULK_INSERT_CHUNK));
    if (insertErr) throw insertErr;
  }

  return rows.length;
}

async function finalizeIfDone(campaign) {
  const pending = await countRecipients(campaign.id, 'pending');
  const sending = await countRecipients(campaign.id, 'sending');
  const inserted = await countRecipients(campaign.id);
  const total = Number(campaign.total_recipients || 0);
  const isSegment = !!(campaign.segment || campaign.search || campaign.state_filter);

  if (pending > 0 || sending > 0) return false;

  // Segment still expected but nobody enqueued — do NOT mark completed (was the 0/0 bug)
  if (isSegment && inserted === 0) {
    const resolved = await ensureSegmentTotal(campaign);
    if (resolved > 0) return false;
    await serviceRoleClient
      .from('whatsapp_bulk_campaigns')
      .update({
        status: 'failed',
        completed_at: new Date().toISOString(),
      })
      .eq('id', campaign.id);
    console.error('[bulk] segment enqueue empty — refresh audience cache / check segment', campaign.segment);
    return true;
  }

  if (inserted < total && isSegment) return false;

  await serviceRoleClient.rpc('whatsapp_bulk_refresh_campaign_stats', { p_campaign_id: campaign.id });
  const { data: stats } = await serviceRoleClient
    .from('whatsapp_bulk_campaigns')
    .select('sent_count, failed_count, total_recipients')
    .eq('id', campaign.id)
    .maybeSingle();

  const statsTotal = Number(stats?.total_recipients || 0);
  const failed = Number(stats?.failed_count || 0);
  const finalStatus = statsTotal > 0 && failed === statsTotal ? 'failed' : 'completed';

  await serviceRoleClient
    .from('whatsapp_bulk_campaigns')
    .update({ status: finalStatus, completed_at: new Date().toISOString() })
    .eq('id', campaign.id);

  return true;
}

export function triggerBulkWorker(campaignId) {
  fetch(`${SUPABASE_URL}/functions/v1/whatsapp-bulk-worker`, {
    method: 'POST',
    headers: fnHeaders,
    body: JSON.stringify({ campaign_id: campaignId }),
  }).catch(() => {});
}

export async function insertBulkRecipients(campaignId, recipients, { messageMode, textBody, trackLinks }) {
  const rows = recipients.map((r) => ({
    campaign_id: campaignId,
    phone: normalizePhone(r.phone),
    contact_name: r.contact_name || r.variables?.customer_name || null,
    variables: r.variables || {},
    status: 'pending',
    target_url: trackLinks && messageMode !== 'template'
      ? extractFirstUrl(fillTemplate(textBody || '', buildVars(r)))
      : null,
  })).filter((r) => r.phone);

  for (let i = 0; i < rows.length; i += BULK_INSERT_CHUNK) {
    const { error } = await serviceRoleClient
      .from('whatsapp_bulk_campaign_recipients')
      .insert(rows.slice(i, i + BULK_INSERT_CHUNK));
    if (error) throw error;
  }

  return rows.length;
}

async function processOneBatch(campaignId, batchIndex = 0) {
  const { data: campaign, error: campErr } = await serviceRoleClient
    .from('whatsapp_bulk_campaigns')
    .select('*')
    .eq('id', campaignId)
    .maybeSingle();
  if (campErr || !campaign) throw campErr || new Error('Campaign not found');
  if (campaign.status === 'completed' || campaign.status === 'failed' || campaign.status === 'cancelled') {
    return { done: true };
  }

  if (campaign.status === 'pending') {
    await serviceRoleClient.from('whatsapp_bulk_campaigns').update({ status: 'running' }).eq('id', campaignId);
  }

  const pendingBefore = await countRecipients(campaignId, 'pending');
  if (pendingBefore < BULK_BATCH_SIZE) {
    await populateFromSegment(campaign);
  }

  let pending = [];
  const { data: claimed, error: claimErr } = await serviceRoleClient.rpc('whatsapp_bulk_claim_recipients', {
    p_campaign_id: campaignId,
    p_limit: BULK_BATCH_SIZE,
  });
  if (!claimErr && Array.isArray(claimed) && claimed.length) {
    pending = claimed;
  } else if (claimErr && !/could not find|does not exist|42883/i.test(claimErr.message || '')) {
    throw claimErr;
  } else {
    const { data, error: pendingErr } = await serviceRoleClient
      .from('whatsapp_bulk_campaign_recipients')
      .select('id, phone, contact_name, variables, target_url')
      .eq('campaign_id', campaignId)
      .eq('status', 'pending')
      .order('created_at', { ascending: true })
      .limit(BULK_BATCH_SIZE);
    if (pendingErr) throw pendingErr;
    pending = data || [];
  }

  if (!pending?.length) {
    const done = await finalizeIfDone(campaign);
    return { done };
  }

  let rateLimited = false;
  let maxRetryAfterMs = 0;
  let currentConcurrency = BULK_CONCURRENCY;

  await mapPool(pending, currentConcurrency, async (recipient) => {
    const sendPayload = buildSendPayload(campaign, recipient);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-send`, {
        method: 'POST',
        headers: fnHeaders,
        body: JSON.stringify(sendPayload),
      });
      const json = await res.json().catch(() => ({}));
      const errText = String(json?.error || json?.message || `HTTP ${res.status}`);

      if (json?.success) {
        await serviceRoleClient
          .from('whatsapp_bulk_campaign_recipients')
          .update({
            status: 'sent',
            delivery_status: 'sent',
            sent_at: new Date().toISOString(),
            wa_message_id: json?.data?.wa_message_id || null,
            error: null,
          })
          .eq('id', recipient.id);
        return;
      }

      if (isRetryableSendFailure(res.status, errText)) {
        rateLimited = true;
        currentConcurrency = BULK_CONCURRENCY_THROTTLED;
        maxRetryAfterMs = Math.max(maxRetryAfterMs, parseRetryAfterMs(errText, res.status));
        // Put back to pending — do NOT count as permanent failed
        await serviceRoleClient
          .from('whatsapp_bulk_campaign_recipients')
          .update({ status: 'pending', error: null })
          .eq('id', recipient.id);
        return;
      }

      await serviceRoleClient
        .from('whatsapp_bulk_campaign_recipients')
        .update({ status: 'failed', error: errText })
        .eq('id', recipient.id);
    } catch (e) {
      const errText = e?.message || 'error';
      if (isRetryableSendFailure(0, errText)) {
        rateLimited = true;
        currentConcurrency = BULK_CONCURRENCY_THROTTLED;
        await serviceRoleClient
          .from('whatsapp_bulk_campaign_recipients')
          .update({ status: 'pending', error: null })
          .eq('id', recipient.id);
      } else {
        await serviceRoleClient
          .from('whatsapp_bulk_campaign_recipients')
          .update({ status: 'failed', error: errText })
          .eq('id', recipient.id);
      }
    }
  });

  if (rateLimited && maxRetryAfterMs > 0) {
    await sleep(maxRetryAfterMs);
  }

  if ((batchIndex + 1) % STATS_REFRESH_EVERY === 0) {
    await serviceRoleClient.rpc('whatsapp_bulk_refresh_campaign_stats', { p_campaign_id: campaignId });
  }

  const remainingPending = await countRecipients(campaignId, 'pending');
  const remainingSending = await countRecipients(campaignId, 'sending');
  const inserted = await countRecipients(campaignId);
  const needsMorePopulation = inserted < Number(campaign.total_recipients || 0)
    && (campaign.segment || campaign.search || campaign.state_filter);

  if (remainingPending === 0 && remainingSending === 0 && !needsMorePopulation) {
    await finalizeIfDone(campaign);
    return { done: true };
  }

  return { done: false, processed: pending.length, rateLimited };
}

async function isCampaignFinished(campaignId) {
  const { data } = await serviceRoleClient
    .from('whatsapp_bulk_campaigns')
    .select('status')
    .eq('id', campaignId)
    .maybeSingle();
  return data?.status === 'completed' || data?.status === 'failed' || data?.status === 'cancelled';
}

/**
 * Drive campaign until done.
 * Prefer a single edge-worker chain. Browser only sends locally if worker stalls
 * (avoids Resume creating a second blast → rate-limit storm → fake failures).
 */
export async function seedMechanicAudience(campaignId) {
  const { data: seedCampaign } = await serviceRoleClient
    .from('whatsapp_bulk_campaigns')
    .select('id, segment, search, state_filter, total_recipients, status')
    .eq('id', campaignId)
    .maybeSingle();
  if (!seedCampaign || !String(seedCampaign.segment || '').startsWith('mechanics')) {
    return 0;
  }
  if (seedCampaign.status === 'pending') {
    await serviceRoleClient
      .from('whatsapp_bulk_campaigns')
      .update({ status: 'running' })
      .eq('id', campaignId);
  }
  let seeded = 0;
  for (let i = 0; i < 30; i += 1) {
    const n = await populateFromSegment(seedCampaign);
    seeded += n;
    if (n <= 0) break;
  }
  if (seeded > 0) {
    await serviceRoleClient.rpc('whatsapp_bulk_refresh_campaign_stats', { p_campaign_id: campaignId }).catch(() => {});
  }
  return seeded;
}

export async function runBulkCampaignProcessor(campaignId) {
  if (activeCampaigns.has(campaignId)) return false;
  activeCampaigns.add(campaignId);

  try {
    await seedMechanicAudience(campaignId);
    triggerBulkWorker(campaignId);

    let lastProgress = -1;
    let stallTicks = 0;
    let stallBatchIndex = 0;

    while (!(await isCampaignFinished(campaignId))) {
      await sleep(4000);

      const { data } = await serviceRoleClient
        .from('whatsapp_bulk_campaigns')
        .select('sent_count, failed_count, status')
        .eq('id', campaignId)
        .maybeSingle();

      if (!data || data.status === 'completed' || data.status === 'failed') break;

      const progress = Number(data.sent_count || 0) + Number(data.failed_count || 0);
      if (progress !== lastProgress) {
        lastProgress = progress;
        stallTicks = 0;
        continue;
      }

      stallTicks += 1;
      // ~12s no progress → one worker kick; ~20s → gentle local batch
      if (stallTicks === 3) {
        triggerBulkWorker(campaignId);
      } else if (stallTicks >= 5) {
        const result = await processOneBatch(campaignId, stallBatchIndex);
        stallBatchIndex += 1;
        if (result.done) break;
        stallTicks = 0;
      }
    }
    return true;
  } catch (err) {
    console.error('[bulkCampaignProcessor]', err);
    return false;
  } finally {
    activeCampaigns.delete(campaignId);
  }
}

export async function resumeBulkCampaign(campaignId) {
  if (activeCampaigns.has(campaignId)) {
    return { ok: true, already_running: true };
  }

  const { data: camp } = await serviceRoleClient
    .from('whatsapp_bulk_campaigns')
    .select('status')
    .eq('id', campaignId)
    .maybeSingle();

  if (camp?.status === 'cancelled' || camp?.status === 'failed') {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_resume_campaign', {
      p_campaign_id: campaignId,
    });
    if (error && !/could not find|does not exist|42883/i.test(error.message || '')) {
      throw error;
    }
    if (data?.success === false) {
      return { ok: false, error: data.error || 'Resume failed' };
    }
    if (error) {
      // RPC not deployed — fallback for cancelled only
      if (camp?.status === 'cancelled') {
        await serviceRoleClient
          .from('whatsapp_bulk_campaign_recipients')
          .update({ status: 'pending', error: null })
          .eq('campaign_id', campaignId)
          .eq('status', 'skipped');
        await serviceRoleClient
          .from('whatsapp_bulk_campaigns')
          .update({ status: 'running', completed_at: null })
          .eq('id', campaignId);
      } else {
        await serviceRoleClient
          .from('whatsapp_bulk_campaigns')
          .update({ status: 'running', completed_at: null })
          .eq('id', campaignId);
      }
    }

    const { error: requeueErr } = await serviceRoleClient.rpc(
      'whatsapp_bulk_requeue_rate_limit_failures',
      { p_campaign_id: campaignId },
    );
    if (requeueErr) {
      console.warn('[resumeBulk] requeue skipped:', requeueErr.message);
    }

    triggerBulkWorker(campaignId);
    void runBulkCampaignProcessor(campaignId);
    return {
      ok: true,
      already_running: false,
      requeued_pending: Number(data?.requeued_pending || 0),
      from_stopped: true,
    };
  }

  // Put rate-limit / stuck "sending" rows back to pending before restarting
  const { error: requeueErr } = await serviceRoleClient.rpc(
    'whatsapp_bulk_requeue_rate_limit_failures',
    { p_campaign_id: campaignId },
  );
  if (requeueErr) {
    console.warn('[resumeBulk] requeue skipped:', requeueErr.message);
  }

  await serviceRoleClient
    .from('whatsapp_bulk_campaigns')
    .update({ status: 'running', completed_at: null })
    .eq('id', campaignId)
    .in('status', ['pending', 'running', 'failed']);

  triggerBulkWorker(campaignId);
  void runBulkCampaignProcessor(campaignId);
  return { ok: true, already_running: false };
}
